// Reserve natural name/colour heights per carousel, or per visual collection row.
const containers = new WeakMap();
const parts = [['name', '--echt-home-name-height'], ['colour', '--echt-home-colour-height']];
const textSelector = '.echt-home-card-title__name > span, .echt-home-card-title__colour > span';

class EchtHomepageCardTitle extends HTMLElement {
  connectedCallback() {
    const container = this.closest('.product-grid, .resource-list[data-testid="resource-list-grid"]');
    if (!container || this.release) return;
    let state = containers.get(container);
    if (!state) {
      const isGrid = container.matches('.product-grid');
      state = { items: new Set(), observed: new Set(), frame: 0 };
      state.schedule = () => {
        cancelAnimationFrame(state.frame);
        state.frame = requestAnimationFrame(() => {
          const observed = new Set([container]);
          const rows = [];
          const desktop = window.matchMedia('(min-width: 768px)').matches;
          for (const item of state.items) {
            for (const text of item.querySelectorAll(textSelector)) observed.add(text);
            const card = isGrid ? item.closest('.product-grid__item') ?? item : item;
            if (isGrid) observed.add(card);
            if (isGrid && !desktop) {
              for (const [, variable] of parts) item.style.removeProperty(variable);
              continue;
            }
            if (!item.getClientRects().length) continue;
            const top = isGrid ? card.getBoundingClientRect().top : 0;
            let row = rows.find(candidate => Math.abs(candidate.top - top) < 1);
            if (!row) {
              row = { top, items: [] };
              rows.push(row);
            }
            row.items.push(item);
          }
          // Keep observations current when filtering replaces text or cards in place.
          for (const element of state.observed) {
            if (!observed.has(element)) state.observer.unobserve(element);
          }
          for (const element of observed) {
            if (!state.observed.has(element)) state.observer.observe(element);
          }
          state.observed = observed;
          for (const row of rows) {
            for (const [part, variable] of parts) {
              const height = Math.max(0, ...row.items.map(item => item.querySelector(`.echt-home-card-title__${part} > span`)?.getBoundingClientRect().height ?? 0));
              const value = `${Math.ceil(height)}px`;
              const targets = isGrid ? row.items : [container];
              for (const target of targets) {
                if (target.style.getPropertyValue(variable) !== value) target.style.setProperty(variable, value);
              }
            }
          }
        });
      };
      state.observer = new ResizeObserver(state.schedule);
      state.mutations = new MutationObserver(state.schedule);
      state.mutations.observe(container, { childList: true, characterData: true, subtree: true });
      // Grid density can change without changing the container's own width.
      state.attributes = new MutationObserver(state.schedule);
      state.attributes.observe(container, { attributes: true, attributeFilter: ['class', 'style', 'product-grid-view'] });
      window.addEventListener('resize', state.schedule);
      containers.set(container, state);
    }
    state.items.add(this);
    document.fonts.ready.then(() => { if (this.isConnected) state.schedule(); });
    state.schedule();
    this.release = () => {
      state.items.delete(this);
      for (const [, variable] of parts) this.style.removeProperty(variable);
      if (!state.items.size) {
        state.observer.disconnect();
        state.mutations.disconnect();
        state.attributes.disconnect();
        window.removeEventListener('resize', state.schedule);
        cancelAnimationFrame(state.frame);
        for (const [, variable] of parts) container.style.removeProperty(variable);
        containers.delete(container);
      } else state.schedule();
    };
  }

  disconnectedCallback() {
    this.release?.();
    this.release = null;
  }
}
if (!customElements.get('echt-homepage-card-title')) customElements.define('echt-homepage-card-title', EchtHomepageCardTitle);
